// ═════════════════════════════════════════════════════════════════════════════
// EVENT / SERVICE HELPERS
//
// Port of the pieces of beleef-frontend's src/utils/event.js that the admin
// Events page needs, so a booking made here is built from the SAME source of
// truth an agent booking is: the property's cart, expanded through packages,
// resolved to products, grouped by contractor service + company.
//
// Kept deliberately close to the frontend original — when that file changes,
// this one should be re-checked against it rather than diverging.
// ═════════════════════════════════════════════════════════════════════════════

// Tailwind dot classes + labels for the media group list (frontend parity).
export const SERVICE_CONFIG = {
  photo: { dot: "bg-[#D85A30]", label: "Photo" },
  video: { dot: "bg-[#7F77DD]", label: "Video" },
  floorplan: { dot: "bg-[#1D9E75]", label: "Floorplan" },
  floorplanRedraw: { dot: "bg-[#1D9E75]", label: "Floorplan Redraw" },
  auction: { dot: "bg-red-600", label: "Auction" },
};

// Handles: raw string, { $oid: "..." }, { _id: "..." }, ObjectId
export function toIdString(val) {
  if (!val) return null;
  if (typeof val === "string") return val;
  if (val.$oid) return val.$oid;
  if (val._id) return toIdString(val._id);
  return val.toString();
}

// A package line stands for its member products. Fan it out so bundled
// products get media groups exactly like individually-bought ones.
export function expandPackageCartItems(cartItems, packages) {
  if (!cartItems?.length) return [];

  const packageById = new Map();
  for (const pkg of packages || []) {
    const id = toIdString(pkg._id);
    if (id) packageById.set(id, pkg);
  }

  const expanded = [];
  for (const item of cartItems) {
    const packageId = toIdString(item.packageId);
    if (!packageId) {
      expanded.push(item);
      continue;
    }

    const pkg = packageById.get(packageId);
    if (!pkg) continue;

    for (const member of pkg.productIds || []) {
      const memberId = toIdString(member);
      if (!memberId) continue;
      expanded.push({
        productId: memberId,
        name: (typeof member === "object" && member.name) || item.name,
        category: (typeof member === "object" && member.category) || "package",
        // The package's company scopes the contractor pool for every member
        // it bundles (mirrors the backend's resolveItems).
        companyOverride: pkg.contractorCompanyId || null,
        fromPackageId: packageId,
        fromPackageName: item.name || pkg.name,
      });
    }
  }
  return expanded;
}

// ═════════════════════════════════════════════════════════════════════════════
// AVAILABLE MEDIA GROUPS
//
// Builds the bookable media rows for one property, from its cart.
//
// Grouping rules (same as the frontend):
//   - group by contractorService AND contractor company — the same service
//     bought from two companies is two jobs done by two different contractors
//   - locked items (already booked, googleEventId + start in the past) go to
//     their own group and are not offered
//   - isScheduled on a group = every item in it is already booked, so picking
//     it is a reschedule rather than a new booking
//
// What is deliberately NOT here:
//   postProduction (floorplan redraw, declutter, virtual furniture, ...),
//   print and portal products. They are purchased work with no on-site
//   attendance — the frontend lists them as tasks with no contractor and no
//   calendar invite. Admin only creates contractor bookings, which always
//   send an invite, so they must never appear as something bookable here.
// ═════════════════════════════════════════════════════════════════════════════
export function buildMediaGroups({
  existingEvents = [],
  products = [],
  carts = [],
  packages = [],
}) {
  const scheduledCeIds = new Set();
  const lockedCeIds = new Set();
  const now = new Date();

  for (const evt of existingEvents) {
    if (evt.state === "cancel") continue;
    for (const svc of evt.services || []) {
      const id = toIdString(svc.campaignEventId);
      if (!id) continue;
      scheduledCeIds.add(id);
      if (evt.googleEventId && evt.start && new Date(evt.start) < now) {
        lockedCeIds.add(id);
      }
    }
  }

  // How many times each campaign event is already booked — tells a
  // "first purchase, already booked" apart from "second purchase of the same
  // product that still needs a booking".
  const scheduledCounts = new Map();
  for (const id of scheduledCeIds) {
    let count = 0;
    for (const evt of existingEvents) {
      if (evt.state === "cancel") continue;
      for (const svc of evt.services || []) {
        if (toIdString(svc.campaignEventId) === id) count++;
      }
    }
    scheduledCounts.set(id, Math.max(count, 1));
  }

  const productById = new Map();
  for (const product of products) {
    const pid = toIdString(product._id);
    if (pid) productById.set(pid, product);
  }

  const allCartItems = expandPackageCartItems(
    (carts || []).flatMap((cart) => cart.items || []),
    packages,
  );

  const accountedScheduled = new Map();
  const groupMap = new Map();

  for (const cartItem of allCartItems) {
    const pid = toIdString(cartItem.productId);
    if (!pid) continue;

    const product = productById.get(pid);
    if (!product || !product.campaignEventId) continue;

    const ce = product.campaignEventId;
    const et = ce.eventTypeId;
    if (!et) continue;

    // Not calendar events at all / not an on-site attendance.
    if (["postProduction", "print", "portal"].includes(et.category)) continue;
    if (!et.requiresContractor || !et.contractorService) continue;

    const ceId = toIdString(ce._id);

    const totalScheduled = scheduledCounts.get(ceId) || 0;
    const alreadyAccounted = accountedScheduled.get(ceId) || 0;
    const isItemScheduled = alreadyAccounted < totalScheduled;
    const isItemLocked = isItemScheduled && lockedCeIds.has(ceId);
    if (isItemScheduled) accountedScheduled.set(ceId, alreadyAccounted + 1);

    const service = et.contractorService;
    const companyKey =
      toIdString(cartItem.companyOverride) ||
      toIdString(product.contractorCompanyId);
    const groupKey = `${service}::${companyKey}${
      isItemLocked ? "__locked" : ""
    }`;

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, {
        items: [],
        campaignEventIds: [],
        contractorService: service,
        contractorCompanyId: companyKey,
        contractorCompanyName:
          (typeof product.contractorCompanyId === "object" &&
            product.contractorCompanyId?.name) ||
          null,
        hasScheduledItems: false,
        hasNewItems: false,
        isLocked: isItemLocked,
      });
    }
    const group = groupMap.get(groupKey);

    if (isItemScheduled) group.hasScheduledItems = true;
    else group.hasNewItems = true;

    group.items.push({
      campaignEventId: ceId,
      // The campaign event names the scheduling bucket ("Floorplan Medium");
      // the product names the job the contractor was actually booked for.
      name: ce.name,
      productName: product.name,
      eventTypeKey: et.key,
      quantity: ce.quantity || 1,
      size: ce.size || null,
      durationMinutes:
        (ce.durationHours ?? 0) * 60 || et.defaultDurationMinutes || 60,
      productId: pid,
    });
    group.campaignEventIds.push(ceId);
  }

  const media = [];
  for (const [groupKey, group] of groupMap) {
    media.push({
      groupKey,
      summary: group.items.map((i) => i.name).join(", "),
      totalDuration: group.items.reduce((s, i) => s + i.durationMinutes, 0),
      contractorService: group.contractorService,
      contractorCompanyId: group.contractorCompanyId,
      contractorCompanyName: group.contractorCompanyName,
      // A mix of booked + new counts as new: saving replaces the lot.
      isScheduled: group.hasScheduledItems && !group.hasNewItems,
      isLocked: group.isLocked,
      items: group.items,
      campaignEventIds: group.campaignEventIds,
    });
  }
  return media;
}

// The services[] payload the backend stores on the event. Same shape agent
// bookings produce, so invites resolve the product by productId.
export const groupToServices = (group) =>
  (group?.items || []).map((item) => ({
    campaignEventId: item.campaignEventId,
    productId: item.productId || null,
    eventTypeKey: item.eventTypeKey,
    quantity: item.quantity || 1,
    size: item.size || null,
    durationMinutes: item.durationMinutes || 60,
  }));

// Calendar title. The campaign event names are what the calendar has always
// shown, and stay stable across price tiers of the same offering.
export const groupToSummary = (group) =>
  (group?.items || []).map((item) => item.name).join(", ");

// Fingerprint of the campaign events a booking covers — order-independent, so
// an existing event can be matched back to the group it came from.
export const serviceFingerprint = (services) =>
  (services || [])
    .map((s) => toIdString(s.campaignEventId))
    .filter(Boolean)
    .sort()
    .join(",");

// Rebuild a group from an event's own services[]. Used when an event was
// booked from something the cart no longer offers (a free shoot, a product
// since removed, a pre-cart booking) — the Edit form must still show what
// this event actually is rather than an empty field.
export const groupFromEvent = (event) => {
  const services = event?.services || [];
  if (services.length === 0) return null;

  const items = services.map((svc) => ({
    campaignEventId: toIdString(svc.campaignEventId),
    name: svc.eventTypeKey || "Service",
    productName: null,
    eventTypeKey: svc.eventTypeKey || null,
    quantity: svc.quantity || 1,
    size: svc.size || null,
    durationMinutes: svc.durationMinutes || 60,
    productId: toIdString(svc.productId),
  }));

  return {
    groupKey: `event::${event._id}`,
    summary: event.summary || items.map((i) => i.name).join(", "),
    totalDuration: items.reduce((s, i) => s + i.durationMinutes, 0),
    // Unknown — the contractor list stays unfiltered rather than empty.
    contractorService: null,
    contractorCompanyId: null,
    contractorCompanyName: null,
    isScheduled: true,
    isLocked: false,
    isFromEvent: true,
    items,
    campaignEventIds: items.map((i) => i.campaignEventId).filter(Boolean),
  };
};

// Contractors who can do this group's work: they offer the service, and they
// belong to the company that sold the product. A group with no company (system
// / in-house product) isn't company-scoped.
export const contractorsForGroup = (contractors, group) => {
  if (!group?.contractorService) return contractors;
  return contractors.filter((c) => {
    if (!c.services?.includes(group.contractorService)) return false;
    if (!group.contractorCompanyId) return true;
    return toIdString(c.companyId) === group.contractorCompanyId;
  });
};
